// What a pointer takes and where it lands, in a view (docs/claude/handles.md,
// *Where a 3D pointer lands*). The 3D view reports what a ray meets as plain
// records (`Viewer3D.hitsAt` → `RayHits`), a section view the sources it draws
// under a point (section.ts `sectionStackAt`); this module alone decides from
// them what a click takes, what Tab cycles, what the hover outlines and where a
// tool, a drop, a paste, the readout or a focus lands — with ONE see-through rule
// (`SeeThrough`, `stops`) — and what a 3D point snaps to (the drawing's snap set,
// as the move gizmo's). DOM-free: test/viewPicks.test.mjs.
import type {
  Drawing,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  isFlatCeiling,
  roomBenchSeats,
  roomCrown,
} from './ceiling';
import {
  hostHeights,
  type Pt3,
  SNAP_NAMES,
} from './handles';
import {
  interiorHeight,
  type PartFigures,
  partSpan,
  polygonBase,
  polygonHeight,
  roomBase,
  wallBase,
  wallHeight,
} from './heights';
import {
  type FrontPick,
  frontPick,
} from './pickStack';
import {
  type SecPart,
  type SecSrc,
  srcKey,
} from './section';

/** How what a ray meets shows: `opaque` a surface that hides what is behind it
 *  (a solid, a glowing fixture); `clear` one seen through (glass, water); `line`
 *  a 1 px line (a connection) — it takes a click, nothing lands on it; `volume`
 *  a pick volume, no surface at all (the hole of the selected room's opening or
 *  doorway, so a click there takes it). */
export type HitLook = 'clear' | 'line' | 'opaque' | 'volume';

/** One thing a ray meets in the 3D view, as the viewer reports it. */
export interface RayHit {
  /** From the eye along the ray (mm). */
  distance: number;
  /** Where (drawing mm: plan x, y and the height z) — on a solid the section
   *  has cut open, on the cut, where the view shows its fill. */
  point: Pt3;
  /** The surface's unit normal there (drawing coordinates); none for a line or
   *  a pick volume. */
  normal?: Pt3;
  /** What it shows: its object and, for a feature, which. */
  src: SecSrc;
  look: HitLook;
}

/** Everything under a pointer in the 3D view: what the ray meets, nearest
 *  first (what is hidden, or cut away by the section, is not there; an underlay
 *  shows no object, so it is not there either), and where it meets the floor
 *  (z 0; null when it does not). */
export interface RayHits {
  hits: readonly RayHit[];
  floor: { point: Pt3; distance: number } | null;
  /** The room or drawn wall whose features a click picks (the selected one: its
   *  holes' pick volumes are in `hits`): its panes, door leaves and holes take a
   *  click, clear or X-rayed — how they are picked. A click's rule, not a
   *  see-through one: a tool still lands behind them. */
  live: string | null;
}

/** What every gesture in the 3D view sees through — the click, Tab, the hover,
 *  a tool's click, a drop, a paste, the readout, a focus, the 3D section's
 *  surface. ONE rule:
 *  - what a live Move carries along with the pointer is not there at all;
 *  - a clear surface and what X-ray sees through (`xray`: as the viewer's X-ray
 *    pass reads it) are seen through: a click takes what stands behind them, a
 *    tool lands behind them. With nothing else there a click still takes them
 *    (a glass-only object, an X-rayed room stay selectable); nothing lands on
 *    them but the Glass and Opening tools' press, whose work is the walls
 *    (`LandRule` `walls`). */
export interface SeeThrough {
  /** What X-ray sees through, by object and feature; null with X-ray off. */
  xray: ((id: string, part: SecPart | undefined) => boolean) | null;
  /** What a live Move carries along with the pointer; null when none. */
  carried: ReadonlySet<string> | null;
}

/** The hits a gesture meets: what a live Move carries left out. */
function present(at: RayHits, see: SeeThrough): RayHit[] {
  return see.carried ? at.hits.filter((h) => !see.carried!.has(h.src.id)) : [...at.hits];
}

/** THE rule for what hides what is behind it: an opaque surface X-ray does not
 *  see through. */
export function stops(h: RayHit, see: SeeThrough): boolean {
  return h.look === 'opaque' && !see.xray?.(h.src.id, h.src.part);
}

/** Whether a click stops at a hit: what hides what is behind it, a line, a pick
 *  volume, or a feature of the object whose features are live (`RayHits.live`). */
function takesClick(h: RayHit, see: SeeThrough, live: string | null): boolean {
  return stops(h, see) || h.look === 'line' || h.look === 'volume' || (!!h.src.part && h.src.id === live);
}

/** What a click takes first: the nearest hit it stops at, else the nearest one
 *  seen (only glass or X-rayed shells there); null over nothing. */
export function clickHit(at: RayHits, see: SeeThrough): RayHit | null {
  const seen = present(at, see);
  return seen.find((h) => takesClick(h, see, at.live)) ?? seen[0] ?? null;
}

/** Every object under the pointer, once each: what a click takes first, then
 *  the others nearest first — what Tab cycles through. */
export function clickOrder(at: RayHits, see: SeeThrough): SecSrc[] {
  const first = clickHit(at, see);
  const out: SecSrc[] = [], keys = new Set<string>();
  for (const h of first ? [first, ...present(at, see)] : []) {
    const k = srcKey(h.src);
    if (!keys.has(k)) { keys.add(k); out.push(h.src); }
  }
  return out;
}

/** How a landing takes the hits: `solid` on the first surface that hides what is
 *  behind it (`stops`) — every tool, a drop, a paste, the readout, a focus, the
 *  3D section —; `walls` on the first surface at all, clear or X-rayed, or the
 *  hole of the selected room's opening (its pick volume; a doorway's is not, the
 *  door's leaf stands there) — the Glass and Opening tools' press, whose work is
 *  the walls. Either, else the floor. */
export type LandRule = 'solid' | 'walls';

/** Where a gesture lands: the point (drawing mm), the surface's normal there
 *  (the floor's on the floor), what it is on (null on the floor) and how far from
 *  the eye. */
export interface Landing { point: Pt3; normal: Pt3 | null; src: SecSrc | null; distance: number }

const UP: Pt3 = { x: 0, y: 0, z: 1 };

/** Where a gesture at the pointer lands by `rule`; null when the ray meets
 *  neither a surface nor the floor. */
export function landOn(at: RayHits, see: SeeThrough, rule: LandRule = 'solid'): Landing | null {
  const lands = (h: RayHit): boolean => (rule === 'solid'
    ? stops(h, see)
    : h.look === 'opaque' || h.look === 'clear' || (h.look === 'volume' && h.src.part?.kind === 'opening'));
  const h = present(at, see).find(lands);
  if (h) return { point: h.point, normal: h.normal ?? null, src: h.src, distance: h.distance };
  return at.floor ? { point: at.floor.point, normal: UP, src: null, distance: at.floor.distance } : null;
}

// ---- The click rule over a stack, in 3D and a section view alike ----

/** THE click rule over what lies under the pointer (pickStack.ts `frontPick`),
 *  in the 3D view (`clickOrder`) and a section view (`sectionStackAt`) alike:
 *  each source as what a click on it takes (`take`: null for one seen that no
 *  click may take — frozen, locked), once each by that (two members of a group
 *  are its one group), front first. */
export function frontPicks<T extends { target: SecSrc }>(stack: readonly SecSrc[], take: (s: SecSrc) => T | null): FrontPick<T> {
  const seen: (T | null)[] = [], keys = new Set<string>();
  for (const s of stack) {
    const p = take(s), key = srcKey(p ? p.target : s);
    if (keys.has(key)) continue;
    keys.add(key);
    seen.push(p);
  }
  return frontPick(seen);
}

/** What the hover over a stack shows: the pick a click takes, or why not — a
 *  locked one in front (`blocked`: nothing outlined), nothing there (a section
 *  view outlines itself), or it is selected already (as in the plan). */
export type HoverOver<T> = { over: 'blocked' | 'nothing' | 'selected' } | { over: 'pick'; pick: T };

export function hoverOver<T extends { target: SecSrc }>(front: FrontPick<T>, selected: readonly SecSrc[]): HoverOver<T> {
  if (front.blocked) return { over: 'blocked' };
  const head = front.head;
  if (!head) return { over: 'nothing' };
  const key = srcKey(head.target);
  return selected.some((s) => srcKey(s) === key) ? { over: 'selected' } : { over: 'pick', pick: head };
}

/** Tab / Shift+Tab (or Alt+click): which of `tab` to select — the one after the
 *  one selected (`dir` −1: before it), wrapping; the first when none of them is
 *  selected; −1 for none there. */
export function cycleNext<T extends { target: SecSrc }>(tab: readonly T[], selected: readonly SecSrc[], dir: 1 | -1): number {
  const n = tab.length;
  if (!n) return -1;
  const had = new Set(selected.map(srcKey));
  const i = tab.findIndex((p) => had.has(srcKey(p.target)));
  return i < 0 ? 0 : (i + dir + n) % n;
}

// ---- Snapping a 3D point to the drawing ----

/** A point a 3D landing may snap to, and its kind (the OSNAP marker's name). */
export interface SnapPoint3 { p: Pt3; kind?: string }

/** What a plan snap point is a point of, for the heights 3D offers it at
 *  (`SNAP_HEIGHTS`): a room's own (its corners, jambs, pane and opening ends, its
 *  ceiling's lines), one of its benches' corners, a part's (a node object's
 *  nodes), a drawn wall's, an extruded shape's. A point of nothing that stands —
 *  a dimension's, a text's, the imported plan's — has none: the floor alone. */
export type SnapOf =
  | { kind: 'bench'; id: string; bench: string }
  | { kind: 'part'; id: string; rise?: number }
  | { kind: 'room'; id: string }
  | { kind: 'shape'; id: string }
  | { kind: 'wall'; id: string };

/** What the snap heights are read from: the drawing, and a part's catalogue
 *  product (a heater's figures fall back to it, as the 3D view builds it). */
export interface SnapHeightsFrom {
  drawing: Pick<Drawing, 'instances' | 'polygons' | 'walls'>;
  template: (inst: ProductInstance) => PartFigures | null;
}

/** What the heights are read from, with each room's bench seats worked out once. */
interface HeightsIn extends SnapHeightsFrom { seats: (room: ProductInstance) => readonly { id: string; seat: number }[] }
function heightsIn(from: SnapHeightsFrom): HeightsIn {
  const seats = new Map<ProductInstance, readonly { id: string; seat: number }[]>();
  return { ...from, seats: (r) => { let s = seats.get(r); if (!s) seats.set(r, s = roomBenchSeats(r)); return s; } };
}
const roomOf = (id: string, from: SnapHeightsFrom): ProductInstance | undefined => from.drawing.instances.find((i) => i.id === id && i.autoBenches);

/** THE heights an object's snap points stand at in 3D (mm above the ground; the
 *  floor is every point's besides), by what they are points of — a Record, so a
 *  new kind of source does not compile until it says:
 *  - a room: its floor, its top and its underside (flat), and the heights it
 *    names — its panes', openings' and doors' tops, a shaped ceiling's eave,
 *    springing and crown —, never its benches' seats (their corners have them);
 *  - a bench: its seat's top (each tier its own) and the room's floor under it;
 *  - a part: its base and its top (`partSpan`: its height from the ground and
 *    its height); a tilted one's corners each its own height, `rise` above
 *    the body's lowest point (anchors.ts: the body's corners as the plan draws
 *    them), its centre its lowest point and its tilted top;
 *  - a drawn wall: its base and its top (a line drawn with the wall tool: none);
 *  - an extruded shape: its base and its top (a flat one: its base). */
const SNAP_HEIGHTS: { [K in SnapOf['kind']]: (of: Extract<SnapOf, { kind: K }>, from: HeightsIn) => number[] } = {
  bench: (of, from) => {
    const r = roomOf(of.id, from), seat = r ? from.seats(r).find((s) => s.id === of.bench) : undefined;
    return r ? [roomBase(r), ...(seat ? [roomBase(r) + seat.seat] : [])] : [];
  },
  part: (of, from) => {
    const i = from.drawing.instances.find((x) => x.id === of.id);
    if (!i) return [];
    const span = partSpan(i, from.template(i));
    return of.rise != null ? [span[0] + of.rise] : span;
  },
  room: (of, from) => {
    const r = roomOf(of.id, from);
    if (!r) return [];
    const b = roomBase(r);
    const named = hostHeights(r).filter((h) => h.name !== SNAP_NAMES.benchSeat).map((h) => h.z);
    return [b, b + roomCrown(r), ...(isFlatCeiling(r) ? [b + interiorHeight(r)] : []), ...named];
  },
  shape: (of, from) => {
    const p = (from.drawing.polygons || []).find((x) => x.id === of.id);
    return p ? [polygonBase(p), polygonBase(p) + polygonHeight(p)] : [];
  },
  wall: (of, from) => {
    const w = (from.drawing.walls || []).find((x) => x.id === of.id);
    return w && w.style !== 'line' ? [wallBase(w), wallBase(w) + wallHeight(w)] : [];
  },
};

/** The heights a snap point of `of` is offered at in 3D: the floor, and its
 *  object's own (`SNAP_HEIGHTS`) — each once, low to high. */
export function snapHeightsOf(of: SnapOf | undefined, from: SnapHeightsFrom): number[] {
  return heightsAt(of, heightsIn(from));
}
function heightsAt(of: SnapOf | undefined, from: HeightsIn): number[] {
  const own = of ? (SNAP_HEIGHTS[of.kind] as (o: SnapOf, f: HeightsIn) => number[])(of, from) : [];
  const all = [0, ...own].filter(Number.isFinite).map((z) => Math.round(z * 1000) / 1000);
  return [...new Set(all)].sort((a, b) => a - b);
}

/** The drawing's snap points (the plan's snap set: each a plan point, its kind
 *  and what it is a point of) in 3D: each at the heights of what it is a point of
 *  (`snapHeightsOf`, read once per object). */
export function liftSnapPoints(plan: readonly { p: Vec2; kind?: string; of?: SnapOf }[], from: SnapHeightsFrom): SnapPoint3[] {
  const out: SnapPoint3[] = [], memo = new Map<string, number[]>(), at = heightsIn(from);
  for (const c of plan) {
    const key = c.of ? `${c.of.kind}|${c.of.id}|${c.of.kind === 'bench' ? c.of.bench : c.of.kind === 'part' ? c.of.rise ?? '' : ''}` : '';
    let hs = memo.get(key);
    if (!hs) memo.set(key, hs = heightsAt(c.of, at));
    for (const z of hs) out.push(c.kind ? { p: { x: c.p.x, y: c.p.y, z }, kind: c.kind } : { p: { x: c.p.x, y: c.p.y, z } });
  }
  return out;
}

/** What the view tells a 3D snap: where a point shows (css px; null off
 *  screen), the eye, and whether the section keeps a point. */
export interface SnapView {
  project: (p: Pt3) => Vec2 | null;
  eye: Pt3;
  keeps: (p: Pt3) => boolean;
}

/** The snap point nearest the pointer on screen, within `reach` px, that the
 *  view shows there: kept by the section, and not behind what the pointer lands
 *  on — no farther from the eye than the landing, by `depthTol` mm. Null for
 *  none (the landing stays where the pointer is). */
export function snapAt(pointer: Vec2, land: Landing, points: readonly SnapPoint3[], view: SnapView, reach: number, depthTol: number): SnapPoint3 | null {
  let best: SnapPoint3 | null = null, bestD = reach;
  const far = land.distance + depthTol;
  for (const c of points) {
    const q = view.project(c.p);
    if (!q) continue;
    const d = Math.hypot(q.x - pointer.x, q.y - pointer.y);
    if (d > bestD) continue;
    if (Math.hypot(c.p.x - view.eye.x, c.p.y - view.eye.y, c.p.z - view.eye.z) > far || !view.keeps(c.p)) continue;
    best = c;
    bestD = d;
  }
  return best;
}
